import { ElectedOfficial, ElectedOfficialsByRole } from "./ElectedOfficialsPage.types"
import type {
    Official,
    OfficialAction,
} from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard/OfficialCard.types"

/**
 * Groups elected officials by their role/title.
 * Creates a catalog of officials organized by their role.
 *
 * @param officials - Array of elected official objects
 * @returns Array of objects with role name and associated officials
 */
export const catalogOfficialsByRole = (officials: ElectedOfficial[]): ElectedOfficialsByRole[] => {
    const roleMap = new Map<string, ElectedOfficial[]>()

    officials.forEach((official) => {
        const roleName = official?.official?.role
        if (!roleName) return

        if (!roleMap.has(roleName)) {
            roleMap.set(roleName, [])
        }

        const roleOfficials = roleMap.get(roleName)
        if (roleOfficials) {
            roleOfficials.push(official)
        }
    })

    return Array.from(roleMap.entries()).map(([name, officials]) => ({
        name,
        officials,
    }))
}

/**
 * Converts an elected official object to OfficialCard props format
 *
 * @param official - ElectedOfficial object from API
 * @returns Official object for OfficialCard component
 */
export const buildOfficialCardProps = (official: ElectedOfficial): Official => {
    const partyLower = official.party?.toLowerCase() || "other"
    let party: "democrat" | "republican" | "independent" | "other" = "other"

    if (partyLower.includes("dem")) {
        party = "democrat"
    } else if (partyLower.includes("rep")) {
        party = "republican"
    } else if (partyLower.includes("ind")) {
        party = "independent"
    }

    return {
        id: official.id,
        name: official.full_name,
        party,
        role: official.official?.role || "Official",
        photoUrl: official.image_url,
    }
}

/**
 * Builds action buttons for an elected official card
 * Order: Facebook, Twitter, Instagram, YouTube, Contact, Website (last)
 *
 * @param official - ElectedOfficial object from API
 * @returns Array of OfficialAction objects
 */
export const buildOfficialActions = (official: ElectedOfficial): OfficialAction[] => {
    const actions: OfficialAction[] = []

    if (official.facebook) {
        actions.push({
            type: "facebook",
            label: "Facebook",
            url: official.facebook,
        })
    }

    if (official.twitter) {
        actions.push({
            type: "twitter",
            label: "Twitter",
            url: official.twitter,
        })
    }

    if (official.instagram) {
        actions.push({
            type: "instagram",
            label: "Instagram",
            url: official.instagram,
        })
    }

    if (official.linkedin) {
        actions.push({
            type: "linkedin",
            label: "LinkedIn",
            url: official.linkedin,
        })
    }

    if (official.youtube) {
        actions.push({
            type: "youtube",
            label: "YouTube",
            url: official.youtube,
        })
    }

    if (official.contact_website) {
        actions.push({
            type: "contact",
            label: "Contact",
            url: official.contact_website,
        })
    }

    // Website is always last
    if (official.website) {
        actions.push({
            type: "website",
            label: "Website",
            url: official.website,
        })
    }

    return actions
}
